<script setup>
import AppLoader from './components/AppLoader.vue';
import { useGlobalStore } from './store/globalStore';

const globalStore = useGlobalStore();
import KeycloakService from './services/keycloakService';
import { useTokenStore } from './store/tokenStore';
import { csCloseNotification, csNotifyTimer } from './utils/ui';
import { TOKEN_EXP_NOTIF_TIME } from './utils/constants';
import {
  ref,
  getCurrentInstance,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue';

const timeoutRef = ref(null);
const notifyTimerId = ref(null);
const instance = getCurrentInstance();
const tokenActivityStore = useTokenStore();

/**
 * Clears all active timers for session management
 */
const clearTimers = () => {
  if (timeoutRef.value) {
    window.clearTimeout(timeoutRef.value);
    timeoutRef.value = null;
  }
  if (notifyTimerId.value) {
    csCloseNotification(instance, notifyTimerId.value);
    notifyTimerId.value = null;
  }
};

/**
 * Handles user clicking "Stay logged in" on the notification
 * Always refreshes the token (user explicitly wants to stay)
 */
const handleStayLoggedIn = async () => {
  // Store the current value to detect if it changes
  const previousRefreshExp = tokenActivityStore.refreshTokenExpirationTime;
  try {
    // User explicitly wants to stay - always refresh, don't check activity
    const refreshed = await KeycloakService.refreshToken();

    const newRefreshExp = tokenActivityStore.refreshTokenExpirationTime;

    // If value changed, the watch already cleared timers and reprogrammed
    // If value didn't change, we need to do it manually
    if (refreshed && newRefreshExp === previousRefreshExp) {
      clearTimers();
      triggerTokenExpiryNotification();
    } else if (refreshed) {
    }
  } catch (error) {
    console.error('Failed to refresh token:', error);
  }
};

/**
 * Handles when the notification timer ends (user didn't respond)
 */
const handleTimerEnded = () => {
  console.log('Notification timer ended, logging out');
  KeycloakService.logOut();
};

const triggerTokenExpiryNotification = async () => {
  const tokenActivityStore = useTokenStore();

  // Prefer refreshTokenExpirationTime (in seconds); fallback to userStore.tokenExpiry
  const refreshExp = tokenActivityStore.refreshTokenExpirationTime ?? null;
  const fallbackExp = Number(tokenActivityStore.tokenExpiry) ?? null;
  const expirySec = refreshExp ?? fallbackExp;

  // Clear previous timeout
  if (timeoutRef.value != null) {
    window.clearTimeout(timeoutRef.value);
    timeoutRef.value = null;
  }

  const timeUntilNotifMs = expirySec * 1000 - Date.now() - TOKEN_EXP_NOTIF_TIME;

  if (timeUntilNotifMs > 0) {
    timeoutRef.value = window.setTimeout(() => {
      // Show logout alert at scheduled time
      openLogoutAlert();
    }, timeUntilNotifMs);
  } else {
    // Expiration already reached or too close - logout immediately
    await KeycloakService.logOut();
  }
};

/**
 * Opens the logout alert notification with countdown timer
 * Per docs: Popup shows X seconds before refresh token expires
 */
const openLogoutAlert = refreshExpTime => {
  // Calculate time values for the notification
  const timeUntilLogoutMs = refreshExpTime * 1000 - Date.now();
  const timeUntilLogoutMinutes = Math.max(
    0,
    Math.ceil(timeUntilLogoutMs / 1000 / 60)
  );
  const countdownMinutes = TOKEN_EXP_NOTIF_TIME / 1000 / 60;

  // Show the notification timer
  notifyTimerId.value = csNotifyTimer(
    instance,
    handleStayLoggedIn, // Called when user clicks to stay logged in
    handleTimerEnded, // Called when timer ends
    timeUntilLogoutMinutes,
    countdownMinutes
  );
};

watch(
  () => tokenActivityStore.refreshTokenExpirationTime,
  () => {
    clearTimers();
    // Reprogram on each expiration change
    void triggerTokenExpiryNotification();
  }
);

onMounted(async () => {
  // Schedule the initial token expiry notification
  void triggerTokenExpiryNotification();
});

// Clean up timeout on unmount
onBeforeUnmount(() => {
  if (timeoutRef.value != null) {
    window.clearTimeout(timeoutRef.value);
    timeoutRef.value = null;
  }
});
</script>

<template>
  <AppLoader v-if="globalStore.getIsGlobalLoaderOn" :loading="true" />
  <Suspense>
    <router-view />
    <template #fallback>
      <AppLoader :loading="true" />
    </template>
  </Suspense>
</template>

<style lang="scss">
nav {
  margin-left: $menuBarWidth;
}

body {
  margin: 0;
  padding: 0;
  width: 100%;
  height: auto;
  font-weight: 400;
  font-size: 1rem;
  box-sizing: border-box;
}
html {
  background-color: aliceblue;
}

html {
  -ms-overflow-style: none; /* Internet Explorer 10+ */
  scrollbar-width: none; /* Firefox */
}
html::-webkit-scrollbar {
  display: none; /* Safari and Chrome */
}

.cs-message-information-primary {
  font-size: $font-1 !important;
  padding: $padding-0-1 !important;
}

.el-card__body {
  padding: 0 !important;
  height: 100%;
}
.cs-link:hover:not(.cs-link-disabled) .cs-link-text {
  text-decoration: none !important;
}
</style>
